/**
 * /execution — Install Domain landing.
 *
 * Lifecycle tabs: Schedule · Execute.
 *   • Schedule → Readiness · Plan · Dispatch (sub-pivot)
 *   • Execute  → ExecutionInstallTodayPage (today-only: Active + Escalations)
 *
 * Resolve was promoted to its own first-class surface at `/resolve`
 * (post-install triage: callbacks · holdbacks · issues · service · friction).
 * Legacy `?tab=resolve` URLs redirect to `/resolve`.
 *
 * Read-only. Reuses canonical resolvers and existing pages — no resolver,
 * writer, RBAC, or governance changes.
 *
 * URL:
 *   ?tab=schedule|execute   (default: execute)
 *   ?focus=readiness|plan|dispatch   (only when tab=schedule; default: readiness)
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { CalendarDays, Send, Boxes } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { CockpitTabs } from "@/components/cockpit/CockpitShell";
import { SubPivot } from "@/components/cockpit/SubPivot";
import { buildOperationalSummary } from "@/data/observability/operationalSummary";

import InstallReadinessCockpit from "@/pages/InstallReadinessCockpit";
import ExecutionInstallTodayPage from "@/pages/execution/ExecutionInstallTodayPage";
import SchedulingDispatchCockpit from "@/components/dashboards/SchedulingDispatchCockpit";
import ProjectCloseoutRoute from "@/pages/ProjectCloseoutRoute";
import { ExecutionEventStreamRail } from "@/components/execution/ExecutionEventStreamRail";
import { UnifiedResolutionDrawer } from "@/components/execution/UnifiedResolutionDrawer";
import { EXECUTION_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isExecutionServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import { useExecutionSummary } from "@/hooks/useExecutionServerPaging";

type TabKey = "schedule" | "execute" | "closeout";
const TAB_ORDER: TabKey[] = ["schedule", "execute", "closeout"];
const TAB_LABEL: Record<TabKey, string> = {
  schedule: "Schedule",
  execute: "Execute",
  closeout: "Closeout",
};

type ScheduleFocusKey = "readiness" | "plan" | "dispatch";
const SCHEDULE_FOCUS_ORDER: ScheduleFocusKey[] = ["readiness", "plan", "dispatch"];

const RESOLVE_FOCUS_REDIRECT: Record<string, string> = {
  callbacks: "/resolve",
  holdbacks: "/resolve?focus=holdbacks",
  issues: "/resolve?focus=issues",
  service: "/resolve?focus=service",
  friction: "/resolve?focus=friction",
};

export default function ExecutionDomainLanding() {
  const { role } = useRole();
  const data = useAppData();
  const location = useBrowserLocationSnapshot();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const useExecutionServerPaging =
    EXECUTION_SERVER_PAGING_ENABLED &&
    isExecutionServerPagedRoute(location.pathname, location.search);
  const executionSummary = useExecutionSummary({ enabled: useExecutionServerPaging });

  // Legacy redirect: ?tab=resolve[&focus=…] → /resolve[?focus=…]
  useEffect(() => {
    if (params.get("tab") !== "resolve") return;
    const focus = params.get("focus") ?? "callbacks";
    const target = RESOLVE_FOCUS_REDIRECT[focus] ?? "/resolve";
    navigate(target, { replace: true });
  }, [params, navigate]);

  const summary = useMemo(
    () =>
      buildOperationalSummary({
        jobs: data.jobs,
        contractors: data.contractors,
        callbacks: data.callbacks,
        holdbackLedger: data.holdbackLedger,
        disputeEvents: data.disputeEvents,
        softSuggestions: data.softSuggestions,
        overrideOutcomes: data.overrideOutcomes,
      }),
    [data.jobs, data.contractors, data.callbacks, data.holdbackLedger, data.disputeEvents, data.softSuggestions, data.overrideOutcomes],
  );

  if (!canAccessExecutionDomain(role)) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Card className="p-6 text-sm text-muted-foreground">
          You don't have access to the Install domain.
        </Card>
      </div>
    );
  }

  const tabCount: Record<TabKey, number | undefined> = {
    schedule: undefined,
    execute: useExecutionServerPaging
      ? executionSummary.data?.activeCount
      : summary.activeJobs,
    closeout: undefined,
  };

  const tabParam = params.get("tab") as TabKey | null;
  const activeTab: TabKey = tabParam && TAB_ORDER.includes(tabParam) ? tabParam : "execute";

  const focusParam = params.get("focus") as string | null;
  const activeScheduleFocus: ScheduleFocusKey =
    focusParam && (SCHEDULE_FOCUS_ORDER as string[]).includes(focusParam)
      ? (focusParam as ScheduleFocusKey)
      : "readiness";

  const tabs = TAB_ORDER.map((k) => ({
    value: k,
    label: TAB_LABEL[k],
    count: tabCount[k],
  }));

  const setTab = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === "execute") next.delete("tab");
    else next.set("tab", v);
    if (v !== "schedule") next.delete("focus");
    setParams(next, { replace: true });
  };

  const setScheduleFocus = (v: ScheduleFocusKey) => {
    const next = new URLSearchParams(params);
    next.set("tab", "schedule");
    if (v === "readiness") {
      next.delete("focus");
      next.delete("lens");
    } else {
      next.set("focus", v);
      next.set("lens", v); // sync embedded SchedulingDispatchCockpit lens
    }
    setParams(next, { replace: true });
  };

  const executeFocus = params.get("focus");
  const executeSuffix =
    activeTab === "execute"
      ? executeFocus === "escalation-signals" || executeFocus === "escalations"
        ? "Escalation Signals"
        : "Active Installs"
      : undefined;
  const scheduleSuffix =
    activeTab === "schedule"
      ? activeScheduleFocus === "plan"
        ? "Plan"
        : activeScheduleFocus === "dispatch"
          ? "Dispatch"
          : "Readiness"
      : undefined;
  const closeoutSuffix = activeTab === "closeout" ? "Closeout" : undefined;
  const titleSuffix = scheduleSuffix ?? executeSuffix ?? closeoutSuffix;

  return (
    <CockpitPage
      title="Install"
      titleSuffix={titleSuffix}
      // Reserve the fixed right-rail's footprint (w-72 + right-6 gutter + 8px
      // breathing room) on xl+ so KPIs, description, and table columns don't
      // slide under it.
      className="xl:pr-[20.5rem]"
      controls={
        activeTab === "execute" ? (
          <div id="install-masthead-controls" className="contents" />
        ) : undefined
      }
      tabs={
        <CockpitTabs tabs={tabs} value={activeTab} onChange={setTab} />
      }
    >
      {activeTab === "schedule" && (
        <>
          <SubPivot<ScheduleFocusKey>
            value={activeScheduleFocus}
            onChange={setScheduleFocus}
            revealKey={`sched-${activeTab}`}
            ariaLabel="Schedule focus"
            options={[
              {
                id: "readiness",
                label: "Readiness",
                sub: "materials · gates",
                icon: <Boxes className="h-3.5 w-3.5" />,
              },
              {
                id: "plan",
                label: "Plan",
                sub: "calendar · book",
                icon: <CalendarDays className="h-3.5 w-3.5" />,
              },
              {
                id: "dispatch",
                label: "Dispatch",
                sub: "today's queue",
                icon: <Send className="h-3.5 w-3.5" />,
              },
            ]}
          />
          {activeScheduleFocus === "readiness" && <InstallReadinessCockpit embedded />}
          {activeScheduleFocus === "plan" && <SchedulingDispatchCockpit embedded hideLensPills />}
          {activeScheduleFocus === "dispatch" && <SchedulingDispatchCockpit embedded hideLensPills />}
        </>
      )}
      {activeTab === "execute" && <ExecutionInstallTodayPage embedded />}
      {activeTab === "closeout" && <ProjectCloseoutRoute embedded headerTargetId="install-masthead-controls" />}
      <ExecutionEventStreamRail />
      <UnifiedResolutionDrawer />
    </CockpitPage>
  );
}
